import { useLocalization } from "@fluent/react"
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import { useRef, useState } from "react"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { StructureNodeTreeDto } from "../structureSlice"
import {
    MAX_TEMPLATE_BYTES,
    problemMessage,
    ReportTemplateDto,
    TemplateValidationReport,
    uploadReportTemplate,
    validateReportTemplate,
} from "./reportTemplatesApi"
import { TemplateReportView } from "./TemplateReportView"

interface UploadNodeReportTemplateDialogProps {
    documentType: string
    documentTypeLabel: string
    node: StructureNodeTreeDto
    /** The unit's current template of this type; the upload then becomes its next version. */
    existing: ReportTemplateDto | null
    onClose: () => void
    onSaved: (template: ReportTemplateDto, report: TemplateValidationReport) => void
}

export const UploadNodeReportTemplateDialog = ({
    documentType,
    documentTypeLabel,
    node,
    existing,
    onClose,
    onSaved,
}: UploadNodeReportTemplateDialogProps) => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const fileInput = useRef<HTMLInputElement>(null)

    const [file, setFile] = useState<File | null>(null)
    const [name, setName] = useState(existing?.name ?? "")
    const [report, setReport] = useState<TemplateValidationReport | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [validating, setValidating] = useState(false)
    const [saving, setSaving] = useState(false)

    const genericError = l10n.getString("report-template-action-error", null, "Operacja nie powiodła się.")

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0]
        // Allow picking the same file again after editing it in Word.
        e.target.value = ""
        if (!selected) {
            return
        }

        setReport(null)
        setError(null)

        if (!selected.name.toLowerCase().endsWith(".docx")) {
            setFile(null)
            setError(l10n.getString("report-template-file-must-be-docx", null, "The file must be a .docx template"))
            return
        }
        if (selected.size > MAX_TEMPLATE_BYTES) {
            setFile(null)
            setError(l10n.getString("report-template-file-too-large", null, "The file is too large (max 10 MB)."))
            return
        }

        setFile(selected)
        if (!name.trim()) {
            setName(selected.name.replace(/\.docx$/i, ""))
        }

        setValidating(true)
        try {
            const result = await dispatch(validateReportTemplate({ documentType, file: selected }))
            if (validateReportTemplate.fulfilled.match(result)) {
                setReport(result.payload)
            } else if (result.payload?.kind === "invalid-template") {
                setReport(result.payload.report)
            } else {
                setError(problemMessage(result.payload) ?? genericError)
            }
        } finally {
            setValidating(false)
        }
    }

    const handleSave = async () => {
        if (!file) {
            return
        }

        setSaving(true)
        setError(null)
        try {
            const result = await dispatch(uploadReportTemplate({
                documentType,
                file,
                name: name.trim() || file.name,
                structureNodeId: node.id,
            }))

            if (uploadReportTemplate.fulfilled.match(result)) {
                onSaved(result.payload.template, result.payload.report)
            } else if (result.payload?.kind === "invalid-template") {
                setReport(result.payload.report)
            } else if (result.payload?.kind === "http" && result.payload.status === 403) {
                setError(l10n.getString("report-template-forbidden", null, "Brak uprawnień do zmiany szablonów raportów."))
            } else {
                setError(problemMessage(result.payload) ?? genericError)
            }
        } finally {
            setSaving(false)
        }
    }

    const busy = validating || saving
    const canSave = !!file && !!report && report.isValid && !busy

    return (
        <Dialog open maxWidth="md" fullWidth onClose={busy ? undefined : onClose}>
            <DialogTitle>
                {existing
                    ? l10n.getString("report-template-upload-version-title", { type: documentTypeLabel }, `Nowa wersja szablonu: ${documentTypeLabel}`)
                    : l10n.getString("report-template-upload-node-title", { type: documentTypeLabel }, `Prześlij szablon: ${documentTypeLabel}`)}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {existing
                            ? l10n.getString(
                                "report-template-upload-version-hint",
                                { node: node.name, version: existing.currentVersion + 1 },
                                `Szablon jednostki „${node.name}” zostanie zapisany jako wersja ${existing.currentVersion + 1}.`,
                            )
                            : l10n.getString(
                                "report-template-upload-node-hint",
                                { node: node.name },
                                `Szablon zostanie przypisany do jednostki „${node.name}”.`,
                            )}
                        {" "}
                        {l10n.getString(
                            "report-template-upload-check-hint",
                            null,
                            "Plik jest sprawdzany zaraz po wybraniu – drobne usterki tagów są poprawiane automatycznie.",
                        )}
                    </Typography>

                    <TextField
                        label={l10n.getString("report-template-name", null, "Name")}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 200 }}
                    />

                    <div>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<UploadFileIcon />}
                            disabled={busy}
                            onClick={() => fileInput.current?.click()}
                        >
                            {l10n.getString("report-template-choose-file", null, "Choose .docx file")}
                        </Button>
                        <input
                            ref={fileInput}
                            type="file"
                            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            hidden
                            onChange={handleFileChange}
                        />
                        {file && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {file.name}
                            </Typography>
                        )}
                    </div>

                    {validating && (
                        <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                {l10n.getString("report-template-validating", null, "Sprawdzanie szablonu…")}
                            </Typography>
                            <LinearProgress />
                        </Stack>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}

                    {report && !validating && <TemplateReportView report={report} />}
                </Stack>
            </DialogContent>
            <DialogActions>
                <CancelButton loading={busy} onClick={onClose} />
                <SaveButton loading={saving} disabled={!canSave} onClick={handleSave} />
            </DialogActions>
        </Dialog>
    )
}
